// Pure model for the chat context meter.
//
// ONE measured basis: the provider-reported prompt fill for the last COMPLETED
// turn (`lastPromptTokens` = input + cache-read + cache-write). That is exactly
// what the model processed — no char estimate, and no conversation/overhead
// split (providers never report that split, so any split is a guess). While a
// turn is in flight the meter keeps showing the last completed turn's number;
// it updates when the turn completes (turn_complete) or when a conversation is
// reopened (hydrated from the persisted lastRequestPromptTokens). Before the
// first measured turn — and right after a summarize clears it — there is no
// number, so the meter hides rather than fabricate one.

export interface ContextMeterMeta {
  /** Provider-measured prompt fill for the last completed turn — the only basis. */
  lastPromptTokens?: number;
  messageCount?: number;
}

export type MeterZone = 'green' | 'amber' | 'red';

export interface ContextMeterModel {
  /** Measured prompt fill in tokens (0 before any measured turn). */
  fillTokens: number;
  /** Full model context window. */
  contextWindow: number;
  /** Fill as a percentage of the full window (0–100). */
  fillPct: number;
  /** Turn-depth dimension. */
  messageCount: number;
  /** Color-journey zone driven by fill. */
  zone: MeterZone;
  /** Passive context-rot risk marker, as a % position on the bar. */
  rotBandPct: number;
  /** True once fill crosses the high (red) threshold. */
  atCapacity: boolean;
  /** False when there is no measured turn yet — hide the meter. */
  hasData: boolean;
}

/**
 * Fill thresholds for the green → amber → red journey, measured against the
 * FULL window. Amber aligns with the default summarize nudge (compactPct 0.7);
 * red flags the approach to the ceiling.
 */
export const AMBER_PCT = 70;
export const RED_PCT = 85;

/**
 * Context-rot risk marker position on the bar. Chroma's context-rot study (18
 * models) puts effective multi-hop recall at ~50–65% of the advertised window;
 * past this band, long-context recall degrades. Passive — a visible cue, never
 * an action.
 */
export const ROT_BAND_PCT = 65;

export function zoneFor(fillPct: number): MeterZone {
  if (fillPct >= RED_PCT) return 'red';
  if (fillPct >= AMBER_PCT) return 'amber';
  return 'green';
}

export function computeContextMeter(meta: ContextMeterMeta, contextWindow: number): ContextMeterModel {
  const win = contextWindow > 0 ? contextWindow : 200_000;
  const fillTokens =
    typeof meta.lastPromptTokens === 'number' && meta.lastPromptTokens > 0 ? Math.min(meta.lastPromptTokens, win) : 0;
  const fillPct = Math.min(100, (fillTokens / win) * 100);

  return {
    fillTokens,
    contextWindow: win,
    fillPct,
    messageCount: meta.messageCount ?? 0,
    zone: zoneFor(fillPct),
    rotBandPct: ROT_BAND_PCT,
    atCapacity: fillPct >= RED_PCT,
    hasData: fillTokens > 0,
  };
}

export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1000)}K`;
  return n.toLocaleString();
}
